'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface Alert {
  id: string;
  type: 'critical' | 'warning' | 'info';
  message: string;
  timestamp: string;
  entity: string;
}

interface AlertBadgeProps {
  className?: string;
}

const AlertBadge = ({ className = '' }: AlertBadgeProps) => {
  const [alertCount, setAlertCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [alerts, setAlerts] = useState<Alert[]>([]);

  useEffect(() => {
    const mockAlerts: Alert[] = [
      {
        id: 'a-001',
        type: 'critical',
        message: 'Transaction variance exceeds threshold',
        timestamp: new Date().toISOString(),
        entity: 'NAO',
      },
      {
        id: 'a-002',
        type: 'warning',
        message: 'Delayed data sync detected',
        timestamp: new Date(Date.now() - 300000).toISOString(),
        entity: 'EUR',
      },
      {
        id: 'a-003',
        type: 'info',
        message: 'Monthly close period approaching',
        timestamp: new Date(Date.now() - 600000).toISOString(),
        entity: 'APG',
      },
    ];

    setAlerts(mockAlerts);
    setAlertCount(mockAlerts.filter((a) => a.type === 'critical' || a.type === 'warning').length);
  }, []);

  const getAlertIcon = (type: Alert['type']) => {
    switch (type) {
      case 'critical':
        return 'ExclamationCircleIcon';
      case 'warning':
        return 'ExclamationTriangleIcon';
      case 'info':
        return 'InformationCircleIcon';
    }
  };

  const getAlertColor = (type: Alert['type']) => {
    switch (type) {
      case 'critical':
        return 'text-error';
      case 'warning':
        return 'text-warning';
      case 'info':
        return 'text-accent';
    }
  };

  const formatTimestamp = (isoString: string) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative flex items-center justify-center w-10 h-10 bg-secondary text-secondary-foreground border border-border rounded-md hover:bg-muted hover:shadow-md transition-all duration-250 ease-out active:scale-98"
        aria-label={`${alertCount} alerts`}
      >
        <Icon name="BellIcon" size={20} className="text-text-secondary" />
        {alertCount > 0 && (
          <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-error text-error-foreground text-xs font-mono font-semibold rounded-full z-badge animate-pulse-glow">
            {alertCount > 99 ? '99+' : alertCount}
          </span>
        )}
      </button>

      {showDropdown && (
        <div className="absolute top-full right-0 mt-2 w-80 sm:w-96 bg-card border border-border rounded-md shadow-xl z-dropdown overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-secondary flex items-center justify-between">
            <h3 className="font-mono text-sm font-semibold text-foreground">Alerts</h3>
            <Link
              href="/anomaly-management-hub"
              onClick={() => setShowDropdown(false)}
              className="font-mono text-xs font-medium text-accent hover:text-accent/80 transition-colors duration-250"
            >
              View All
            </Link>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {alerts.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <Icon name="CheckCircleIcon" size={32} className="mx-auto text-success mb-2" />
                <p className="font-sans text-sm text-text-secondary">No active alerts</p>
              </div>
            ) : (
              alerts.map((alert) => (
                <Link
                  key={alert.id}
                  href="/anomaly-management-hub"
                  onClick={() => setShowDropdown(false)}
                  className="block px-4 py-3 border-b border-border hover:bg-secondary transition-all duration-250 ease-out"
                >
                  <div className="flex items-start gap-3">
                    <Icon
                      name={getAlertIcon(alert.type) as any}
                      size={20}
                      className={`flex-shrink-0 mt-0.5 ${getAlertColor(alert.type)}`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-sans text-sm text-foreground mb-1">{alert.message}</p>
                      <div className="flex items-center gap-2 text-xs text-text-secondary">
                        <span className="font-data">{alert.entity}</span>
                        <span>•</span>
                        <span className="font-sans">{formatTimestamp(alert.timestamp)}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AlertBadge;